import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  FilterBar,
  FilterSearch,
  InlineStats,
  Input,
  Modal,
  Select,
  StatusPill,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Tr
} from '../../components/admin/ui';
import { useConfirm, useToast } from '../../components/admin/overlays';
import { IconBan, IconCheck, IconCourier, IconPlus } from '../../components/admin/icons';
import { formatDate } from '../../components/admin/labels';
import { fieldErrorsFrom } from '../../utils/formErrors';

const emptyForm = { name: '', email: '', website: '', accountManager: '' };

const planTone = (plan) => (plan === 'intelligence' ? 'success' : 'info');
const planLabel = (plan) => (plan === 'intelligence' ? 'Intelligence' : 'Pilot');

/**
 * Admin management of courier-PROVIDER business accounts (Pilot/Intelligence
 * billing) — distinct from the "Couriers" lookup-table page, which just
 * manages the dropdown list (Evri, DPD, ...) used on the issue-report form.
 */
const CourierAccountManagement = () => {
  const confirm = useConfirm();
  const toast = useToast();

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  // ?q= seeds the search so other screens can link to one account — the
  // transactions ledger sends you here by email when you click a courier.
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQueryState] = useState(() => searchParams.get('q') || '');
  const setQuery = (value) => {
    setQueryState(value);
    setSearchParams(value ? { q: value } : {}, { replace: true });
  };
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [createErrors, setCreateErrors] = useState({});
  const [busyId, setBusyId] = useState(null);

  // "Access" dialog: the carrier link and the portal login, which are the two
  // things standing between a new account and a usable courier portal.
  const [linkFor, setLinkFor] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [linkForm, setLinkForm] = useState({ courier: '', loginName: '', loginEmail: '', loginPassword: '' });
  const [linkUsers, setLinkUsers] = useState([]);
  const [linkErrors, setLinkErrors] = useState({});
  const [linkSaving, setLinkSaving] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);

  const fetchAccounts = useCallback(async () => {
    try {
      const { data } = await api.get('/api/admin/courier-accounts');
      setAccounts(data || []);
    } catch (error) {
      toast('Could not load courier accounts.', { tone: 'error' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const openLink = async (account) => {
    setLinkFor(account);
    setLinkErrors({});
    setInviteLink(null);
    setLinkForm({ courier: account.courier || '', loginName: '', loginEmail: account.email || '', loginPassword: '' });
    try {
      const [{ data: courierList }, { data: users }] = await Promise.all([
        api.get('/api/admin/couriers'),
        api.get(`/api/admin/courier-accounts/${account._id}/users`)
      ]);
      setCarriers(courierList || []);
      setLinkUsers(users || []);
    } catch {
      toast('Could not load carriers or logins.', { tone: 'error' });
    }
  };

  const saveCarrier = async () => {
    setLinkSaving(true);
    try {
      const { data } = await api.patch(`/api/admin/courier-accounts/${linkFor._id}`, {
        courier: linkForm.courier || null
      });
      setAccounts((current) => current.map((a) => (a._id === data._id ? data : a)));
      setLinkFor(data);
      toast(data.courier ? `Linked to ${data.courier}.` : 'Carrier link cleared.', { tone: 'success' });
    } catch (error) {
      toast(error.response?.data?.error || 'Could not save the carrier link.', { tone: 'error' });
    } finally {
      setLinkSaving(false);
    }
  };

  const createLogin = async () => {
    setLinkSaving(true);
    setLinkErrors({});
    try {
      const { data } = await api.post(`/api/admin/courier-accounts/${linkFor._id}/users`, {
        name: linkForm.loginName.trim() || undefined,
        email: linkForm.loginEmail.trim(),
        ...(linkForm.loginPassword && { password: linkForm.loginPassword })
      });
      setLinkUsers((current) => [...current, data]);
      setInviteLink(data.setPasswordLink);
      setLinkForm((f) => ({ ...f, loginName: '', loginPassword: '' }));
      toast(
        data.setPasswordLink
          ? 'Login created — share the invitation link below.'
          : 'Login created; they can sign in now.',
        { tone: 'success' }
      );
    } catch (error) {
      setLinkErrors(fieldErrorsFrom(error, { fallback: 'Could not create the login.' }));
    } finally {
      setLinkSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter(
      (a) => a.name?.toLowerCase().includes(q) || a.email?.toLowerCase().includes(q)
    );
  }, [accounts, query]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setCreateErrors({});
    try {
      const { data } = await api.post('/api/admin/courier-accounts', form);
      setAccounts((current) => [data, ...current]);
      setShowCreate(false);
      setForm(emptyForm);
      toast('Courier account created.', { tone: 'success' });
    } catch (error) {
      setCreateErrors(fieldErrorsFrom(error, { fallback: 'Could not create the courier account.' }));
    } finally {
      setCreating(false);
    }
  };

  const handleCancelSubscription = async (account, atPeriodEnd) => {
    const ok = await confirm({
      title: atPeriodEnd ? 'Cancel at period end' : 'Cancel now',
      message: atPeriodEnd
        ? `${account.name} keeps Intelligence until the period they have paid for ends, then drops back to the free level.`
        : `${account.name} loses Intelligence immediately and drops back to the free level. They are not billed again.`,
      confirmLabel: atPeriodEnd ? 'Cancel at period end' : 'Cancel now',
      tone: 'danger'
    });
    if (!ok) return;

    setBusyId(account._id);
    try {
      const { data } = await api.post(
        `/api/admin/courier-accounts/${account._id}/cancel-subscription`,
        { atPeriodEnd }
      );
      setAccounts((current) => current.map((a) => (a._id === data.account._id ? data.account : a)));
      toast(atPeriodEnd ? 'Will cancel at period end.' : 'Subscription cancelled.', {
        tone: 'success'
      });
    } catch (error) {
      toast(error.response?.data?.error || 'Could not cancel the subscription.', { tone: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const handleDeclineCancellation = async (account) => {
    const ok = await confirm({
      title: 'Decline cancellation request',
      message: `Keep ${account.name} on Intelligence and close their request. They can raise a new one.`,
      confirmLabel: 'Decline request'
    });
    if (!ok) return;

    setBusyId(account._id);
    try {
      const { data } = await api.post(
        `/api/admin/courier-accounts/${account._id}/decline-cancellation`
      );
      setAccounts((current) => current.map((a) => (a._id === data.account._id ? data.account : a)));
      toast('Request declined.', { tone: 'success' });
    } catch (error) {
      toast(error.response?.data?.error || 'Could not decline the request.', { tone: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const handleSuspendToggle = async (account) => {
    setBusyId(account._id);
    try {
      const { data } = await api.patch(`/api/admin/courier-accounts/${account._id}/suspend`, {
        suspended: !account.isSuspended
      });
      setAccounts((current) => current.map((a) => (a._id === data._id ? data : a)));
      toast(data.isSuspended ? 'Account suspended.' : 'Account unsuspended.', { tone: 'success' });
    } catch (error) {
      toast('Could not update the account.', { tone: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (account) => {
    const ok = await confirm({
      title: 'Delete courier account',
      message: `Delete ${account.name}? This cannot be undone.`,
      confirmLabel: 'Delete',
      tone: 'danger'
    });
    if (!ok) return;

    setBusyId(account._id);
    try {
      await api.delete(`/api/admin/courier-accounts/${account._id}`);
      setAccounts((current) => current.filter((a) => a._id !== account._id));
      toast('Courier account deleted.', { tone: 'success' });
    } catch (error) {
      toast(error.response?.data?.error || 'Could not delete the account.', { tone: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const pendingRequests = accounts.filter((a) => a.cancellationRequest?.status === 'pending');

  return (
    <div className="space-y-6">
      {/* Couriers can't cancel themselves — they raise a request and it lands
          here, so someone sees the reason before anything is switched off.
          Same arrangement as merchants on the Subscriptions screen. */}
      {pendingRequests.length > 0 && (
        <Card>
          <CardHeader
            title={`Cancellation requests (${pendingRequests.length})`}
            description="Couriers waiting on a decision. Nothing is cancelled until you act here."
          />
          <div className="divide-y divide-ink-200">
            {pendingRequests.map((account) => (
              <div key={account._id} className="p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-600 text-ink-900">{account.name}</p>
                    <p className="mt-0.5 text-sm text-ink-500">{account.email}</p>
                    <p className="mt-2 text-sm text-ink-600">
                      Asked to cancel{' '}
                      <span className="font-600 text-ink-900">
                        {account.cancellationRequest.timing === 'immediate'
                          ? 'as soon as possible'
                          : 'at the end of the billing period'}
                      </span>
                      {account.cancellationRequest.requestedAt &&
                        ` on ${formatDate(account.cancellationRequest.requestedAt)}`}
                      .
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={busyId === account._id}
                      onClick={() => handleDeclineCancellation(account)}
                    >
                      Decline
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={busyId === account._id}
                      onClick={() => handleCancelSubscription(account, true)}
                    >
                      Cancel at period end
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={busyId === account._id}
                      onClick={() => handleCancelSubscription(account, false)}
                    >
                      Cancel now
                    </Button>
                  </div>
                </div>

                {account.cancellationRequest.note && (
                  <blockquote className="mt-3 whitespace-pre-wrap rounded-xl bg-ink-50 px-4 py-3 text-sm leading-relaxed text-ink-700">
                    {account.cancellationRequest.note}
                  </blockquote>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <FilterBar
          actions={
            <>
              <InlineStats
                items={[
                  {
                    label: query ? 'Matching' : 'Accounts',
                    value: loading ? '—' : filtered.length
                  }
                ]}
              />
              <Button size="sm" onClick={() => setShowCreate(true)}>
                <IconPlus size={15} />
                New courier account
              </Button>
            </>
          }
        >
          <FilterSearch
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name or email"
            className="min-w-[16rem]"
          />
        </FilterBar>

        {loading ? (
          <TableSkeleton cols={6} rows={5} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={IconCourier}
            title="No courier accounts yet"
            description="Create one to give a courier provider a free pilot. They upgrade to Intelligence themselves, from their own portal."
          />
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <Th>Account</Th>
                <Th>Plan</Th>
                <Th>Carrier</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((account) => (
                <Tr key={account._id}>
                  <Td>
                    <span className="block font-600 text-ink-900">{account.name}</span>
                    <span className="block text-xs text-ink-500">{account.email}</span>
                  </Td>
                  <Td>
                    <StatusPill tone={planTone(account.plan)}>{planLabel(account.plan)}</StatusPill>
                    {account.plan === 'pilot' && account.freeCaseAllowance != null && (
                      <span className="mt-1 block text-xs text-ink-500">
                        {account.pilotCasesUsed || 0} of {account.freeCaseAllowance} free cases used
                      </span>
                    )}
                  </Td>
                  <Td>
                    {/* Without this link the portal has no way to tell which
                        cases are theirs, so it is surfaced as its own column
                        rather than buried in an edit form. */}
                    {account.courier ? (
                      <StatusPill tone="neutral">{account.courier}</StatusPill>
                    ) : (
                      <button
                        type="button"
                        onClick={() => openLink(account)}
                        className="rounded text-xs font-600 text-warning-700 underline transition-colors hover:text-warning-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
                      >
                        Not linked — set carrier
                      </button>
                    )}
                  </Td>
                  <Td>
                    <StatusPill tone={account.isSuspended ? 'danger' : account.isActive ? 'success' : 'neutral'}>
                      {account.isSuspended ? 'Suspended' : account.isActive ? 'Active' : 'Inactive'}
                    </StatusPill>
                  </Td>
                  <Td className="text-ink-600">{formatDate(account.createdAt)}</Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => openLink(account)}
                      >
                        Access
                      </Button>
                      <Button
                        size="sm"
                        variant={account.isSuspended ? 'secondary' : 'dangerGhost'}
                        loading={busyId === account._id}
                        onClick={() => handleSuspendToggle(account)}
                      >
                        {account.isSuspended ? (
                          <>
                            <IconCheck size={14} />
                            Unsuspend
                          </>
                        ) : (
                          <>
                            <IconBan size={14} />
                            Suspend
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="dangerGhost"
                        loading={busyId === account._id}
                        onClick={() => handleDelete(account)}
                      >
                        Delete
                      </Button>
                    </div>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>

      {/* Access: carrier link + portal login */}
      <Modal
        open={Boolean(linkFor)}
        onClose={() => setLinkFor(null)}
        title={linkFor ? `Access — ${linkFor.name}` : 'Access'}
        description="A courier sees nothing until their account is linked to a carrier and has a login."
        size="lg"
      >
        <div className="space-y-5">
          <section>
            <h3 className="font-display text-xs font-700 uppercase tracking-[0.08em] text-ink-500">
              Carrier link
            </h3>
            <p className="mt-1.5 text-sm leading-relaxed text-ink-600">
              Which carrier on the report form this account may see. Cases record the carrier by
              name, so this is what scopes their dashboard to their own data.
            </p>
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <Field label="Carrier" htmlFor="link-courier" className="min-w-[14rem] flex-1">
                <Select
                  id="link-courier"
                  value={linkForm.courier}
                  onChange={(e) => setLinkForm({ ...linkForm, courier: e.target.value })}
                >
                  <option value="">Not linked</option>
                  {carriers.map((c) => (
                    <option key={c._id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Button onClick={saveCarrier} loading={linkSaving}>
                <IconCheck size={15} />
                Save link
              </Button>
            </div>
          </section>

          <section className="border-t border-ink-100 pt-5">
            <h3 className="font-display text-xs font-700 uppercase tracking-[0.08em] text-ink-500">
              Portal logins
            </h3>

            {linkUsers.length > 0 && (
              <ul className="mt-3 space-y-2">
                {linkUsers.map((u) => (
                  <li
                    key={u.id || u._id}
                    className="flex items-center justify-between rounded-lg bg-ink-50 px-3.5 py-2.5"
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-600 text-ink-900">{u.name}</span>
                      <span className="block text-xs text-ink-500">{u.email}</span>
                    </span>
                    <StatusPill tone={u.status === 'active' ? 'success' : 'warning'}>
                      {u.status === 'active' ? 'Active' : 'Invited'}
                    </StatusPill>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-3 grid gap-4 rounded-xl bg-ink-50 p-4 sm:grid-cols-2">
              <Field label="Their name" htmlFor="link-login-name" error={linkErrors.name}>
                <Input
                  id="link-login-name"
                  value={linkForm.loginName}
                  onChange={(e) => setLinkForm({ ...linkForm, loginName: e.target.value })}
                  placeholder="Dana Hill"
                />
              </Field>
              <Field label="Login email" htmlFor="link-login-email" required error={linkErrors.email}>
                <Input
                  id="link-login-email"
                  type="email"
                  value={linkForm.loginEmail}
                  onChange={(e) => setLinkForm({ ...linkForm, loginEmail: e.target.value })}
                />
              </Field>
              <Field
                label="Password"
                htmlFor="link-login-password"
                error={linkErrors.password}
                hint="Leave blank to generate an invitation link instead."
                className="sm:col-span-2"
              >
                <Input
                  id="link-login-password"
                  type="text"
                  value={linkForm.loginPassword}
                  onChange={(e) => setLinkForm({ ...linkForm, loginPassword: e.target.value })}
                  placeholder="Minimum 8 characters"
                  autoComplete="new-password"
                />
              </Field>
              <div className="sm:col-span-2">
                <Button onClick={createLogin} loading={linkSaving} size="sm">
                  <IconPlus size={15} />
                  Create login
                </Button>
              </div>
            </div>

            {linkErrors._general && (
              <p role="alert" className="mt-3 text-sm text-danger-700">
                {linkErrors._general}
              </p>
            )}

            {inviteLink && (
              <div className="mt-3 rounded-xl bg-info-50 p-3.5 ring-1 ring-inset ring-info-100">
                <p className="text-sm font-600 text-ink-900">Invitation link</p>
                <p className="mt-1 break-all font-mono text-xs text-ink-700">{inviteLink}</p>
                <p className="mt-1.5 text-xs text-ink-500">
                  Send this to them — it lets them set their own password.
                </p>
              </div>
            )}
          </section>
        </div>
      </Modal>

      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="New courier account"
        description="Creates the account on a free Pilot. Start the Intelligence subscription from the table once they're ready to pay."
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <Field label="Business name" htmlFor="courier-name" required error={createErrors.name}>
            <Input
              id="courier-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              autoFocus
            />
          </Field>
          <Field label="Email" htmlFor="courier-email" required error={createErrors.email}>
            <Input
              id="courier-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </Field>
          <Field label="Website" htmlFor="courier-website" error={createErrors.website}>
            <Input
              id="courier-website"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
            />
          </Field>
          <Field label="Account manager" htmlFor="courier-am" error={createErrors.accountManager}>
            <Input
              id="courier-am"
              value={form.accountManager}
              onChange={(e) => setForm({ ...form, accountManager: e.target.value })}
            />
          </Field>

          <div className="flex justify-end gap-3 border-t border-ink-100 pt-4">
            <Button type="button" variant="secondary" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={creating}>
              Create account
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CourierAccountManagement;
